import { Component, ElementRef, ViewChild } from '@angular/core';
import { DefaultBUPayload, PlatformRegionService } from '../../../service/shared/platform-region.service';
import { Market, ProductPlatform, Region } from '../../../model/shared/platform-region.model';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { firstValueFrom, map, Observable, startWith } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule, MatOptionSelectionChange } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ChatService } from '../../../service/chat-service';
import { UserProfile } from '../../../model/rolePlay.model';
import { InteractionService } from '../../../service/interaction/interaction.service';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ProfileInfoComponent } from '../../profile-info/profile-info.component';

@Component({
  selector: 'app-product-platform',
  templateUrl: './product-platform.component.html',
  styleUrl: './product-platform.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule
  ]
})
export class ProductPlatformComponent {

  regionMarketForm: FormGroup = new FormGroup({
    regionSelectFormControl: new FormControl([null]),
    regionSearchTextboxControl: new FormControl([null]),
    marketSelectFormControl: new FormControl([null]),
    marketSearchTextboxControl: new FormControl([null]),
  });

  regionData: string[] = [''];
  regionFilteredOptions: Observable<any[]>;
  regionSelectedValues = "";
  selectedRegionsString: string;

  marketData: Market[] = [];
  marketFilteredOptions: Observable<any[]>;
  marketValues: any;
  marketSelectedValues = [];

  @ViewChild('searchRegion') regionSearchTextBox: ElementRef;
  @ViewChild('searchMarket') marketSearchTextBox: ElementRef;

  product_platform_list: ProductPlatform[] = [];

  userProfile!: UserProfile | any;
  userId = Number(sessionStorage.getItem('userId'));

  selected_product: ProductPlatform | null = null;
  selected_product_code: string | null = null;

  isRegionListDisabled: boolean = false;
  isMarketListDisabled: boolean = false;
  region_list: Region[] = [];
  selected_Region: Region | null = null;

  get hasSelectedPlatform(): boolean {
    return !!this.selected_product_code;
  }

  get isDefaultPlatformOrSelectedPlatformSet(): boolean {
    return !!(this.selected_product_code || this.platformRegionService.default_platform_code);
  }

  constructor(
    private platformRegionService: PlatformRegionService,
    private ChatService: ChatService,
    public intService: InteractionService,
    private readonly toastr: ToastrService,
    public dialogRef: MatDialogRef<ProfileInfoComponent>
  ) { }

  ngOnInit() {
    this.setupRegionFilterSubscribers();
    this.setupMarketFilterSubscribers();
    this.loadData();
  }

  getFormControlValue(controlName, dataType) {
    let controlvalue = this.regionMarketForm.controls[controlName].value;
    if (dataType === 'string' && controlvalue && controlvalue !== undefined && controlvalue !== null) {
      return controlvalue;
    } else if (dataType === 'array' && controlvalue.length && controlvalue !== undefined && controlvalue !== null) {
      return controlvalue.toString();
    } else {
      return null
    }
  }

  loadData() {
    this.product_platform_list = this.platformRegionService.productPlatformList;

    this.selected_product_code = this.platformRegionService.default_platform_code;

    if (this.platformRegionService.default_platform_code) {
      this.selected_product = this.platformRegionService.getPlatformProductByProductCode(this.selected_product_code);
      this.selectProduct(this.selected_product, true);
    }
  }

  setupRegionFilterSubscribers() {
    this.regionFilteredOptions = this.regionMarketForm.controls['regionSearchTextboxControl'].valueChanges
      .pipe(
        startWith<string>(''),
        map((name: any) => this._filterRegion(name))
      );
  }

  setupMarketFilterSubscribers() {
    this.marketFilteredOptions = this.regionMarketForm.controls['marketSearchTextboxControl'].valueChanges
      .pipe(
        startWith<string>(''),
        map((name: any) => this._filterMarket(name))
      );
  }

  selectProduct(product: ProductPlatform, initialLoad: boolean = false) {
    console.log('ProductPlatformComponent : selectProduct: ', product);
    if (this.selected_product_code == product.business_unit_code && !initialLoad) {
      this.selected_product_code = null;
      this.selected_product = null;
      this.resetMarketData();
      this.resetRegionData();
    } else {
      this.selected_product_code = product.business_unit_code;
      this.selected_product = product;
      this.loadRegions();
    }

  }

  resetRegionData() {
    this.regionData = [];
    this.region_list = [];
    this.isRegionListDisabled = false;
    this.regionMarketForm.controls['regionSelectFormControl'].enable();
    this.selected_Region = null;

    this.regionSelectedValues = '';
    this.regionMarketForm.controls['regionSelectFormControl'].setValue('');
    this.selectedRegionsString = this.regionSelectedValues;
  }

  loadRegions() {
    this.resetRegionData();
    this.region_list = this.selected_product.regions;
    this.regionData = this.region_list.map(region => region.label || region.name);
    this.isRegionListDisabled = this.regionData && this.regionData.length == 1;
    let region: Region = this.selected_product.regions[0];

    if (this.selected_product.user_default_region) {
      region = this.getRegionByRegionCode(this.selected_product.user_default_region) || region;
    }

    this.updateRegion(region);
    this.loadMarket(region.name, this.selected_product.user_default_market)
    this.setupRegionFilterSubscribers();
  }

  getRegionByRegionCode(region_code: string): Region {
    return this.selected_product.regions.find(region => region.name === region_code || region.label === region_code);
  }

  updateRegion(region: Region) {
    this.selected_Region = region;
    this.regionSelectedValues = region.label || region.name;
    this.regionMarketForm.controls['regionSelectFormControl'].setValue(region.label || region.name);
    this.selectedRegionsString = region.name;

    if (this.isRegionListDisabled) {
      this.regionMarketForm.controls['regionSelectFormControl'].disable();
    }
  }

  private _filterRegion(name: string): String[] {
    const filterValue = name.toLowerCase();
    this.regionSetSelectedValues();
    this.regionMarketForm.controls['regionSelectFormControl'].setValue(this.regionSelectedValues);
    let filteredList = this.regionData.filter(option => option.toLowerCase().indexOf(filterValue) === 0);
    return filteredList;
  }

  regionClearSearch(event) {
    event.stopPropagation();
    this.regionMarketForm.controls['regionSearchTextboxControl'].patchValue('');
  }

  regionSetSelectedValues() {
    this.regionSelectedValues = this.regionMarketForm.controls['regionSelectFormControl'].value;

  }

  regionSelectionChange(event: MatOptionSelectionChange, option: string) {
    if (event.isUserInput) {
      if (event.source.selected) {
        const region: Region = this.getRegionByRegionCode(option);
        this.updateRegion(region);
      }
      this.selectedRegionsString = this.selected_Region?.name || '';
    }
  }

  regionOpenedChange(e) {
    this.regionMarketForm.controls['regionSearchTextboxControl'].patchValue('');
    if (e == true) {
      this.regionSearchTextBox.nativeElement.focus();
    } else {
      if (this.regionMarketForm.controls['regionSelectFormControl'].value && this.regionMarketForm.controls['regionSelectFormControl'].value !== null && this.regionMarketForm.controls['regionSelectFormControl'].value !== undefined) {
        this.loadMarket(this.selectedRegionsString, this.selected_product.user_default_market);
      } else {
        this.resetMarketData();
      }
    }
  }

  resetMarketData() {
    this.marketData = [];
    this.marketSelectedValues = [];
    this.regionMarketForm.controls['marketSelectFormControl'].setValue([]);
    this.regionMarketForm.controls['marketSelectFormControl'].enable();
  }

  async loadMarket(region: string, user_default_market: string) {
    this.resetMarketData()
    this.marketData = await firstValueFrom(this.platformRegionService.getPrimaryMarket(region));
    this.isMarketListDisabled = this.marketData && this.marketData.length == 1;
    let market_code = this.marketData[0].primary_market.code;

    if (this.isMarketListDisabled) {
      this.regionMarketForm.controls['marketSelectFormControl'].disable();
    }

    if (user_default_market) {
      let user_market_code = null;
      const market_code_list: string[] = user_default_market.split(',');
      this.marketData.forEach((market: Market) => {
        if (market_code_list.includes(market.primary_market.code)) {
          user_market_code = user_market_code ? `${user_market_code},${market.primary_market.code}` : market.primary_market.code
        }
      });
      if (user_market_code) {
        market_code = user_market_code;
      }
    }

    this.marketSelectedValues = market_code.split(',');
    this.regionMarketForm.controls['marketSelectFormControl'].setValue(this.marketSelectedValues);
    this.setupMarketFilterSubscribers();
  }

  private _filterMarket(name: string): Market[] {
    const filterValue = (typeof name === 'string' ? name : String(name ?? '')).trim().toLowerCase();
    let filteredList = this.marketData;
    if (name) {
      this.marketSetSelectedValues();
      filteredList = this.marketData.filter((option: any) => option?.primary_market?.name.toLowerCase().indexOf(filterValue) === 0);
      this.regionMarketForm.controls['marketSelectFormControl'].setValue(this.marketSelectedValues);
    }

    return filteredList;
  }

  marketSetSelectedValues() {
    if (this.regionMarketForm.controls['marketSelectFormControl'].value && this.regionMarketForm.controls['marketSelectFormControl'].value.length > 0) {
      this.regionMarketForm.controls['marketSelectFormControl'].value.forEach((e) => {
        if (this.marketSelectedValues.indexOf(e) == -1) {
          this.marketSelectedValues.push(e);
        }
      });
    }
  }

  marketSelectionChange(event) {
    if (event.isUserInput && event.source.selected == false) {
      let index = this.marketSelectedValues.indexOf(event.source.value);
      this.marketSelectedValues.splice(index, 1)
    }
  }

  marketClearSearch(event) {
    event.stopPropagation();
    this.regionMarketForm.controls['marketSearchTextboxControl'].patchValue('');
  }

  getMarketNameByCode(code: any) {
    if (code == null) {
      return null;
    }
    let filteredVal: any = [];
    if (Array.isArray(code)) {
      const codes: string[] = [];
      code.forEach(cd => {
        this.marketData?.forEach((market: any) => {
          if (cd != null && market.primary_market?.code?.toString() === cd.toString()) {
            codes.push(market.primary_market.name);
          }
        });
      });
      filteredVal = codes;
    } else {
      const filteredName: any = this.marketData?.find((market: any) =>
        market.primary_market?.code?.toString() === code.toString()
      );
      filteredVal = filteredName?.primary_market?.name ?? null;
    }
    return filteredVal;
  }


  marketOpenedChange(e) {
    this.regionMarketForm.controls['marketSearchTextboxControl'].patchValue('');
    const selectedValues = this.regionMarketForm.controls['marketSelectFormControl'].value;
    this.marketValues = selectedValues.join(', ');
    if (e == true) {
      this.marketSearchTextBox.nativeElement.focus();
    }
  }

  setDefaultBU() {
    this.setBUMarketAndRegion(true);
  }

  setDefaultMarketAndRegion() {
    this.setBUMarketAndRegion(false);
  }

  setBUMarketAndRegion(is_default_bu: boolean) {
    if (!this.hasSelectedPlatform) {
      this.toastr.error('Select at least one platform group to update.', '', { positionClass: 'toast-top-right' });
      return;
    }

    const selectedMarkets = this.regionMarketForm.controls['marketSelectFormControl'].value || [];
    const market_selected = selectedMarkets.join(',');
    let payload: DefaultBUPayload = {
      wwid: this.userId,
      business_unit_code: this.selected_product_code,
      default_business_unit_code: this.selected_product_code,
      default_primary_region: this.selectedRegionsString ? this.selectedRegionsString : '',
      default_primary_market: market_selected,
      is_default_bu: is_default_bu
    }

    this.ChatService.updateDefaultBUMarket(payload).subscribe({
      next: (response) => {
        if (payload.is_default_bu) {
          this.toastr.success('Default platform, region, and market has been updated!', '', { positionClass: 'toast-top-right' });
          this.platformRegionService.setDefaultPlatformCode(payload.default_business_unit_code, payload.default_primary_region, payload.default_primary_market);
        } else {
          this.toastr.success('Default Region and market has been updated!', '', { positionClass: 'toast-top-right' });
        }
        this.fetchPlatformData();
        this.startNewChatSession();
        this.dialogRef?.close(true);
      },
      error: (error) => {
        this.toastr.error('Failed to update default platform, region, and market.', '', { positionClass: 'toast-top-right' });
      }
    });
  }

  async fetchPlatformData() {
    const user_profile = await firstValueFrom(this.ChatService.getUserProfileByWWID());
    this.platformRegionService.productPlatformList = user_profile.product;
    this.platformRegionService.setSelectedPlatformProductByProductCode(this.platformRegionService.selected_platform_code)
  }

  startNewChatSession() {
    const chatIdentifier = 'newchat';
    this.intService.setChatStatus({ chat_identifier: chatIdentifier });
  }
}
